/**
 * Shared notification helpers used by both the notifications page
 * and the notifications popover/sheet.
 */

import type { NotificationType } from "@shared/types";
import type { LucideIcon } from "lucide-react";
import {
	AlertCircle,
	Briefcase,
	CreditCard,
	FileText,
	MessageCircleWarning,
	MessageSquare,
} from "lucide-react";

/**
 * Format a date string as a relative time string (e.g. "5m ago", "2h ago").
 */
export function timeAgo(dateString: string): string {
	const seconds = Math.floor(
		(Date.now() - new Date(dateString).getTime()) / 1000,
	);
	if (seconds < 60) return "Just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d ago`;
	return new Date(dateString).toLocaleDateString("en-IN");
}

/**
 * Return the appropriate icon for a notification type.
 */
export function getNotificationIcon(type: NotificationType): LucideIcon {
	switch (type) {
		case "request":
		case "boq_sent":
		case "boq_lines_approved":
		case "boq_approved":
		case "boq_changes_requested":
		case "boq_change_request_resolved":
			return FileText;
		case "proposal":
		case "proposal_accepted":
		case "proposal_rejected":
		case "message":
			return MessageSquare;
		case "project_published":
			return Briefcase;
		case "project_funded":
		case "payment_released":
		case "dispute_resolved":
			return CreditCard;
		case "whatsapp_disconnected":
		case "whatsapp_broken":
			return MessageCircleWarning;
		default:
			return AlertCircle;
	}
}
